import type { AnnotationEntry } from '@geosoft/viewer-sdk';
import { usePluginStore } from '@/state/usePluginStore';
import { summarizeProfile } from './profilePlugin';

interface ProfilePanelProps {
  readonly annotations: ReadonlyArray<AnnotationEntry>;
}

export function ProfilePanel({ annotations }: ProfilePanelProps) {
  const { 
    engineType, 
    engineReady, 
    isPluginAvailable, 
    getPluginState,
    setPluginState
  } = usePluginStore();
  
  const available = isPluginAvailable('profile');
  const state = getPluginState('profile');
  const profile = summarizeProfile(annotations);

  const handleGenerateProfile = async () => {
    setPluginState('profile', 'computing');
    
    // Simulate API call - in real implementation this would call BE-122
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setPluginState('profile', 'idle');
    } catch {
      setPluginState('profile', 'error');
    }
  };

  if (!available) {
    return (
      <div className="plugin-not-available">
        <div className="plugin-status-banner">
          <span className="material-symbols-outlined">warning</span>
          <div>
            <strong>Profile not available</strong>
            <p>
              {!engineType && 'Map engine not initialized'}
              {engineType && !engineReady && `${engineType} engine loading...`}
              {engineType && engineReady && `${engineType} engine does not support profile generation`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="plugin-engine-info">
        <span className="engine-badge">
          <span className="material-symbols-outlined">
            {engineType === 'maplibre' ? 'map' : engineType === 'cesium' ? 'language' : 'layers'}
          </span>
          {engineType} engine
        </span>
        {state === 'computing' && (
          <span className="computing-indicator">
            <span className="material-symbols-outlined">hourglass_empty</span>
            Generating profile...
          </span>
        )}
      </div>

      <p>Profile generation samples elevation data along a route using {engineType} terrain services.</p>

      <div className="profile-route-picker">
        <h4>Route Selection</h4>
        <div className="plugin-help">
          Pick a route on the map by clicking points, then generate the elevation profile.
        </div>
      </div>

      <div className="plugin-actions">
        <button
          type="button"
          className="primary"
          onClick={handleGenerateProfile}
          disabled={profile.status === 'no route' || state === 'computing'}
        >
          <span className="material-symbols-outlined">
            {state === 'computing' ? 'hourglass_empty' : 'show_chart'}
          </span>
          {state === 'computing' ? 'Generating...' : 'Generate Profile'}
        </button>

        {profile.status !== 'no route' && (
          <button
            type="button"
            disabled={state === 'computing'}
          >
            <span className="material-symbols-outlined">open_in_new</span>
            Open Profile Viewer
          </button>
        )}
      </div>

      <dl className="plugin-measurements" aria-label="Profile summary">
        <div>
          <dt>
            <span className="material-symbols-outlined">info</span>
            Status
          </dt>
          <dd>{profile.status}</dd>
        </div>
        <div>
          <dt>
            <span className="material-symbols-outlined">route</span>
            Line
          </dt>
          <dd>{profile.label}</dd>
        </div>
        <div>
          <dt>
            <span className="material-symbols-outlined">straighten</span>
            Distance
          </dt>
          <dd>{profile.distanceLabel}</dd>
        </div>
        <div>
          <dt>
            <span className="material-symbols-outlined">analytics</span>
            Samples
          </dt>
          <dd>{profile.sampleCount}</dd>
        </div>
      </dl>

      {state === 'error' && (
        <div className="plugin-error-banner">
          <span className="material-symbols-outlined">error</span>
          <div>
            <strong>Profile generation failed</strong>
            <p>Check route validity and terrain data availability.</p>
          </div>
        </div>
      )}
    </>
  );
}
